import { BlogArticleDetail } from "@/components/pages/blogDetail/BlogArticleDetail.page";
import { type PreviewType } from "@/pages/api/preview";
import { getAllBlogArticles } from "@/services/content/getAllBlogArticles";
import { getBlogArticle, type GetBlogArticleResult } from "@/services/content/getBlogArticle";
import { getBlogArticleDetailPage, type GetBlogArticleDetailPageResult } from "@/services/content/getBlogArticleDetailPage";
import { getCommonProps, type GetCommonPropsResult } from "@/services/content/getCommonProps";
import { type IGenBlogArticle } from "@/services/graphql/__generated/sdk";

import { type GetStaticPaths, type GetStaticPathsResult, type GetStaticProps } from "next";
import React, { type FunctionComponent } from "react";

import { type ParsedUrlQuery } from "querystring";

interface Params extends ParsedUrlQuery
{
  slug: string;
}

export const getStaticPaths: GetStaticPaths<Params> = async () =>
{
  const allBlogs = await getAllBlogArticles("published", "de");

  const paths: GetStaticPathsResult<Params>["paths"] = allBlogs
    .filter(blog => blog.slug !== "404")
    .map(blog => ({
      params: {
        slug: blog.slug!,
      }
    }));

  return {
    fallback: true,
    paths
  };
};

export type GetBlogArticlePropsResult = { readonly blogArticles: IGenBlogArticle[]} & GetBlogArticleResult & GetCommonPropsResult & GetBlogArticleDetailPageResult;

export const getStaticProps: GetStaticProps<GetBlogArticlePropsResult, Params> = async ({ draftMode, locale = "de", params }) =>
{
  const slug = params?.slug;

  if(!slug)
  {
    return {
      notFound: true,
      revalidate: false,
    };
  }

  const previewMode: PreviewType = draftMode ? "draft" : "published";
  const allBlogArticles = await getAllBlogArticles(previewMode, locale);
  const blogArticleProps = await getBlogArticle({ locale, previewMode, slug });
  const blogArticleDetailPageProps = await getBlogArticleDetailPage(locale, previewMode);
  const commonProps = await getCommonProps({ locale, previewMode });

  if(blogArticleProps.resultType === "notFound" || blogArticleDetailPageProps.resultType === "notFound")
  {
    return {
      notFound: true,
      revalidate: false,
    };
  }

  return {
    props: {
      blogArticles: allBlogArticles,
      ...blogArticleProps,
      ...blogArticleDetailPageProps,
      ...commonProps,
    },
    revalidate: 1,
  };
};

const NextjsPage: FunctionComponent<GetBlogArticlePropsResult> = props =>
{
  return <BlogArticleDetail {...props}/>;
};

export default NextjsPage;
